import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ROLES } from '@/features/auth';
import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { usePasswordPolicy } from '@/shared/api/passwordPolicy';
import { Button, Field, Skeleton, useToastStore } from '@/shared/ui';

import type { UserAccountState, UserSummary } from '../model/types';
import {
  useChangeUserLogin,
  useChangeUserState,
  useSetUserPassword,
  useSetUserRoles,
  useUsers,
} from '../queries/useIdentity';
import styles from './Settings.module.css';
import { SectionHead } from './SettingsScreen';

/**
 * Użytkownicy — **kartoteka z widoczną kolejką**, nie sama kolejka.
 *
 * ⭐ Do 2026-09-03 ekran otwierał się na filtrze „czekające" i był w praktyce wyłącznie
 * listą do wpuszczenia. To było prawdziwe, dopóki kont było trzydzieści. Po imporcie
 * (dec. 106 i dalej) kont są setki, a admin przychodzi tu **znaleźć człowieka**, nie
 * przejrzeć kolejkę — więc domyślnym widokiem są konta czynne, a robota do zrobienia
 * melduje się **paskiem z licznikiem** nad tabelą.
 *
 * ⛔ **Świadomie NIE ma drugiej pozycji w menu** („do wpuszczenia"). Byłby to drugi ekran
 * o tych samych wierszach i tych samych akcjach, a kolejka bywa pusta tygodniami — wisiałby
 * wtedy w nawigacji martwy punkt. Pasek pojawia się i znika razem z robotą.
 *
 * ⚠ Szukanie i stronicowanie są **serwerowe**: lista czytała wcześniej wszystkie konta i całe
 * tabele ról oraz członkostw bez żadnego `WHERE`.
 */

/**
 * Role nadawane z tego ekranu. ⛔ **Bez `user`** — to nie jest uprawnienie, tylko stan wyjściowy
 * konta, więc przełącznik przy nim sugerowałby wybór, którego nie ma.
 *
 * Kolejność od najszerszej: pierwszy wiersz plakietek odpowiada na pytanie „kto tu rządzi".
 */
const GRANTABLE_ROLES = [ROLES.admin, ROLES.architect, ROLES.leader] as const;

/**
 * Stan konta → akcja, którą admin ma pod ręką.
 *
 * ⭐ Tabela zamiast łańcucha `if`-ów, z tego samego powodu co przy trybach serwisowych hosta:
 * nowy stan ma tu **nie skompilować się** bez decyzji, zamiast po cichu wpaść w gałąź domyślną.
 */
const ACTION_FOR_STATE = {
  pending: 'activate',
  active: 'disable',
  disabled: 'enable',
} as const satisfies Record<UserAccountState, 'activate' | 'disable' | 'enable'>;

/**
 * Stan konta → klasa plakietki. ⚠ Kolor **wzmacnia** słowo, nigdy go nie zastępuje (dec. 58).
 */
const STATE_BADGE = {
  pending: 'badgePending',
  active: 'badgeOff',
  disabled: 'badgeDisabled',
} as const satisfies Record<UserAccountState, string>;

type Filter = UserAccountState | 'all';

/**
 * ⚠ Kolejność jest kolejnością użycia, nie alfabetu: „aktywne" jest domyślne, więc stoi
 * pierwsze; „wszystkie" na końcu, bo to worek, a nie widok roboczy.
 *
 * ⭐ „Zablokowane" to osobna pozycja, a nie część „nieczynnych" (2026-09-04): konto czekające
 * na wpuszczenie i konto, któremu admin świadomie odebrał dostęp, to dwie różne listy robocze
 * — jedną się przegląda i wpuszcza, drugą się zamknęło.
 */
const FILTERS: readonly Filter[] = ['active', 'pending', 'disabled', 'all'];

/** Ile kont na stronie. ⚠ Sufit serwera to 100 — tutaj mieści się ekran bez przewijania. */
const PAGE_SIZE = 25;

/** Filtr ekranu → parametr serwera; „wszystkie" znaczy „bez zawężenia". */
function stateParam(filter: Filter): UserAccountState | undefined {
  return filter === 'all' ? undefined : filter;
}

export function UsersSection() {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();

  const [filter, setFilter] = useState<Filter>('active');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);

  // ⭐ Szukajka odpytuje serwer z opóźnieniem, a nie na każdy klawisz: przy setkach kont
  //    pisanie „kowalski" to osiem zapytań, z których siedem jest nieaktualnych, zanim wrócą.
  useEffect(() => {
    const handle = setTimeout(() => {
      setQuery(search.trim());
      setOffset(0);
    }, 250);

    return () => clearTimeout(handle);
  }, [search]);

  const users = useUsers({ state: stateParam(filter), q: query, offset, limit: PAGE_SIZE });

  // Licznik kolejki niezależny od bieżącego filtra — jedno tanie zapytanie o samą liczbę.
  // ⚠ `limit: 1`, bo interesuje nas wyłącznie `total`; wierszy z niego nie rysujemy.
  //
  // ⭐ `hasPassword: true` jest częścią definicji kolejki, nie optymalizacją: konto bez hasła
  //    wchodzi LINKIEM aktywacyjnym, a nie przyciskiem admina — „Wpuść" na nim odmawia. Po
  //    imporcie takich kont będą setki i bez tego zawężenia pasek meldowałby robotę, której
  //    nie ma, a kolejka przestałaby być listą rzeczy do zrobienia.
  const pending = useUsers({ state: 'pending', hasPassword: true, q: '', offset: 0, limit: 1 });
  const pendingCount = pending.data?.total ?? 0;

  const page = users.data;
  const from = page && page.total > 0 ? page.offset + 1 : 0;
  const to = page ? Math.min(page.offset + page.items.length, page.total) : 0;

  function changeFilter(next: Filter) {
    setFilter(next);
    setOffset(0);
  }

  return (
    <>
      <SectionHead titleKey="settings.section.users" leadKey="settings.users.lead" />

      {/*
        Pasek roboczy. ⛔ Przy zerze NIE rysujemy „0 czeka" — komunikat o braku roboty jest
        szumem, a pasek ma znikać razem z powodem, dla którego istnieje.
      */}
      {pendingCount > 0 && filter !== 'pending' ? (
        <div className={styles['workBar']}>
          <span>{t('settings.users.pendingBar', { count: pendingCount })}</span>
          <Button variant="ghost" onClick={() => changeFilter('pending')}>
            {t('settings.users.pendingBarAction')}
          </Button>
        </div>
      ) : null}

      <section className={styles['card']}>
        <header className={styles['cardHead']}>
          <h2 className={styles['cardTitle']}>{t('settings.users.title')}</h2>

          <div className={styles['toolbar']}>
            <span className={styles['search']}>
              <Field
                label={t('settings.users.search')}
                labelHidden
                type="search"
                value={search}
                placeholder={t('settings.users.searchPlaceholder')}
                onChange={(event) => setSearch(event.target.value)}
              />
            </span>

            <span className={styles['choices']}>
              {FILTERS.map((option) => (
                <Button
                  key={option}
                  variant={filter === option ? 'primary' : 'ghost'}
                  onClick={() => changeFilter(option)}
                >
                  {t(`settings.users.filter.${option}`)}
                </Button>
              ))}
            </span>
          </div>
        </header>

        {users.isPending ? (
          <div className={styles['cardBody']}>
            <div className={styles['loading']} aria-busy="true">
              <Skeleton height="2.5rem" announce />
              <Skeleton height="2.5rem" />
            </div>
          </div>
        ) : users.isError ? (
          <div className={styles['cardBody']}>
            <div className={styles['failure']} role="alert">
              <p>{describeError(users.error)}</p>
              <Button onClick={() => void users.refetch()}>{t('common.action.retry')}</Button>
            </div>
          </div>
        ) : page!.items.length === 0 ? (
          <p className={styles['empty']}>
            {query
              ? t('settings.users.empty.search', { query })
              : t(`settings.users.empty.${filter}`)}
          </p>
        ) : (
          <>
            <table className={styles['table']}>
              <thead>
                <tr>
                  <th scope="col">{t('settings.users.column.person')}</th>
                  <th scope="col">{t('settings.users.column.state')}</th>
                  <th scope="col">{t('settings.users.column.teams')}</th>
                  <th scope="col">{t('settings.users.column.since')}</th>
                  {/* Kolumna akcji nie ma widocznego nagłówka, ale czytnik ekranu
                      musi wiedzieć, czym jest — inaczej ogłasza pustą komórkę. */}
                  <th scope="col">
                    <span className="visually-hidden">{t('settings.users.column.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {page!.items.map((user) => (
                  <UserRow key={user.id} user={user} />
                ))}
              </tbody>
            </table>

            {/*
              Stopka stronicowania. ⚠ Zakres i całość razem („1–25 z 312"), bo sam numer
              strony nie mówi, ile jeszcze zostało — a to jest jedyne pytanie, jakie się tu
              zadaje.
            */}
            <div className={styles['cardFoot']}>
              <span className={styles['login']}>
                {t('settings.users.range', { from, to, total: page!.total })}
              </span>

              <span className={styles['pager']}>
                <Button
                  variant="ghost"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  {t('common.action.prev')}
                </Button>
                <Button
                  variant="ghost"
                  disabled={to >= page!.total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  {t('common.action.next')}
                </Button>
              </span>
            </div>
          </>
        )}
      </section>
    </>
  );
}

function UserRow({ user }: { user: UserSummary }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const changeState = useChangeUserState();
  const [open, setOpen] = useState(false);

  return (
    <>
      <tr>
        <td className={styles['name']}>
          {user.displayName}
          <div className={styles['login']}>{user.login}</div>
        </td>

        <td>
          {/*
            ⭐ Trzy stany, trzy napisy (2026-09-04). Do tej daty „nieczynne" znaczyło naraz
            *czeka na wpuszczenie* i *odebraliśmy dostęp* — jeden napis nieprawdziwy w połowie
            wierszy. Kolor jest tu WZMOCNIENIEM, nie jedynym nośnikiem: obok stoi słowo (dec. 58).
          */}
          <span className={`${styles['badge']} ${styles[STATE_BADGE[user.state]]}`}>
            {t(`settings.users.state.${user.state}`)}
          </span>

          {/*
            Plakietka na KAŻDĄ nadaną rolę, nie tylko na admina. Przy płaskich zbiorach
            (decyzja 81) konto bywa naraz architektem i liderem, więc jedna plakietka
            pokazywałaby część prawdy — a to gorsze niż brak plakietki.
          */}
          {GRANTABLE_ROLES.filter((role) => user.roles.includes(role)).map((role) => (
            <span key={role} className={`${styles['badge']} ${styles['badgeAdmin']}`}>
              {t(`settings.users.roleName.${role}`)}
            </span>
          ))}
        </td>

        {/*
          Zespoły przy koncie — to przez nie zadanie trafia „na kogoś" (spec 9.1), więc
          bez tej kolumny lista kont nie mówi o człowieku nic poza tym, że istnieje.
        */}
        <td className={styles['login']}>
          {user.teams.length === 0
            ? t('settings.users.noTeams')
            : user.teams.map((team) => team.name).join(' · ')}
        </td>

        <td className={styles['login']}>{user.createdAtUtc.slice(0, 10)}</td>

        <td className={styles['actions']}>
          {/*
            ⭐ Akcja zależy od STANU, nie od jednej flagi:
            czeka → „Wpuść" · żywe → „Zablokuj" · zablokowane → „Odblokuj".

            ⛔ Konto czekające **bez hasła** nie dostaje „Wpuść" — wchodzi linkiem aktywacyjnym,
            a serwer i tak odmówiłby (`identity.user.no_password`). Rysowanie przycisku, który
            zawsze odmawia, to zapraszanie do odmowy; obok idzie zdanie, co z takim kontem zrobić.
          */}
          {user.state === 'pending' && !user.hasPassword ? (
            <span className={styles['emptyInline']}>{t('settings.users.awaitingLink')}</span>
          ) : (
            <Button
              variant={user.state === 'pending' ? 'primary' : 'ghost'}
              disabled={changeState.isPending}
              onClick={() =>
                changeState.mutate(
                  { id: user.id, action: ACTION_FOR_STATE[user.state] },
                  {
                    onSuccess: () =>
                      show({
                        message: t(`settings.users.done.${ACTION_FOR_STATE[user.state]}`, {
                          name: user.displayName,
                        }),
                        tone: 'success',
                      }),
                    onError: (error) => show({ message: describeError(error), tone: 'danger' }),
                  },
                )
              }
            >
              {t(`settings.users.action.${ACTION_FOR_STATE[user.state]}`)}
            </Button>
          )}

          {/*
            ⭐ Reszta akcji chowa się pod jednym przełącznikiem, zamiast rozpychać wiersz na
            pięć przycisków. Przy setkach kont wiersz musi dać się przebiec wzrokiem — akcje
            rzadkie (hasło, adres, usunięcie) są rzadkie właśnie dlatego, że są poważne.
          */}
          <Button variant="ghost" aria-expanded={open} onClick={() => setOpen((shown) => !shown)}>
            {t('settings.users.action.manage')}
          </Button>

          <RoleToggles user={user} />
        </td>
      </tr>

      {open ? (
        <tr>
          <td colSpan={5}>
            <div className={styles['manage']}>
              <PasswordForm user={user} />
              <LoginForm user={user} />
              {/*
                ⛔ Usuwania konta TU NIE MA i to jest decyzja (werdykt usera 2026-09-04, uchyla
                dec. 164): konto zablokowane jest PAMIĘCIĄ organizacji, po której import poznaje,
                że tego człowieka świadomie tu nie chcemy. Skasowany wiersz znaczyłby, że następny
                przebieg zakłada go na nowo i po cichu przypisuje mu własność. Zostaje „Zablokuj"
                — odwracalne.
              */}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * Przełączniki ról — **jeden na rolę, niezależnie**.
 *
 * ⭐ Do tej pory był tu JEDEN przycisk przerzucający konto między „admin" a „user", bo role
 * były dwie i wykluczały się nawzajem. Przy płaskich zbiorach (decyzja 81) to przestało być
 * prawdą: ktoś bywa naraz architektem i liderem, więc przełącznik binarny **kasowałby jedną
 * rolę przy nadawaniu drugiej** — po cichu, bo nikt by nie zauważył, że coś zniknęło.
 *
 * ⛔ Odmowa przychodzi z serwera i jest tu **normalnym wynikiem**: nie odbierzesz roli sobie
 * ani ostatniemu aktywnemu adminowi. Front tego nie powiela, bo „ostatni admin" to pytanie
 * do bazy, a nie do widoku.
 */
function RoleToggles({ user }: { user: UserSummary }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const setRoles = useSetUserRoles();

  function toggle(role: string) {
    const held = user.roles.includes(role);
    const next = held ? user.roles.filter((r) => r !== role) : [...user.roles, role];

    // ⚠ Endpoint przyjmuje PEŁNY zestaw, nie różnicę — semantyka zastępowania. `user`
    // dokładamy zawsze: to stan wyjściowy konta, a nie uprawnienie, i bez niego odebranie
    // ostatniej roli zostawiałoby konto z pustą listą, czyli stanem, którego rejestracja
    // ani seed nigdy nie produkują.
    const roles = [...new Set([...next, ROLES.user])];

    setRoles.mutate(
      { userId: user.id, roles },
      {
        onSuccess: () =>
          show({
            message: t('settings.users.roleChanged', { name: user.displayName }),
            tone: 'success',
          }),
        onError: (error) => show({ message: describeError(error), tone: 'danger' }),
      },
    );
  }

  return (
    <>
      {GRANTABLE_ROLES.map((role) => (
        <Button
          key={role}
          variant="ghost"
          disabled={setRoles.isPending}
          // Wzorzec przełącznika z WAI-ARIA: stan niesie `aria-pressed`, a nie zmieniająca
          // się etykieta. Etykieta „administrator" znaczy zawsze to samo — inaczej czytnik
          // ekranu ogłaszałby czasownik („nadaj") zamiast stanu („wciśnięty").
          aria-pressed={user.roles.includes(role)}
          onClick={() => toggle(role)}
        >
          {t(`settings.users.roleName.${role}`)}
        </Button>
      ))}
    </>
  );
}

/**
 * Ustawienie hasła przez admina.
 *
 * ⚠ To **jedyna** droga do zmiany hasła w produkcie — nie ma maili ani linków resetujących
 * (spec sek. 10). Admin ustala hasło po weryfikacji tożsamości poza systemem i przekazuje
 * je tą samą drogą, więc formularz mówi to wprost, zamiast udawać samoobsługę.
 */
function PasswordForm({ user }: { user: UserSummary }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const setPassword = useSetUserPassword();
  // ⭐ Minimum z serwera, nie ze stałej: do 2026-08-22 front mówił „8", a host wymagał 10
  //   (komentarz w `shared/api/passwordPolicy.ts`). Zanim polityka dojedzie, formularz nie
  //   udaje, że zna próg — serwer i tak rozstrzyga, a jego odmowa niesie `args.minLength`.
  const min = usePasswordPolicy().data?.minPasswordLength;

  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);

  const error =
    touched && min !== undefined && value.trim().length < min
      ? t('settings.users.passwordTooShort', { min })
      : undefined;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);

    if (min !== undefined && value.trim().length < min) {
      return;
    }

    setPassword.mutate(
      { userId: user.id, newPassword: value },
      {
        onSuccess: () => {
          setValue('');
          setTouched(false);
          show({
            message: t('settings.users.passwordSet', { name: user.displayName }),
            tone: 'success',
          });
        },
        onError: (failure) => show({ message: describeError(failure), tone: 'danger' }),
      },
    );
  }

  return (
    <form className={styles['inlineForm']} onSubmit={onSubmit} noValidate>
      <span className={styles['grow']}>
        <Field
          label={t('settings.users.newPassword', { name: user.displayName })}
          type="password"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          hint={
            min !== undefined
              ? `${t('auth.validation.passwordMin', { min })} ${t('settings.users.passwordHint')}`
              : t('settings.users.passwordHint')
          }
          {...(error ? { error } : {})}
        />
      </span>

      {/* ⚠ Nazwa mówi, CO zapisuje: w rozwiniętym wierszu stoją dwa formularze, więc dwa
          przyciski „Zapisz" byłyby nierozróżnialne — także dla czytnika ekranu. */}
      <Button type="submit" variant="primary" disabled={setPassword.isPending}>
        {t('settings.users.savePassword')}
      </Button>
    </form>
  );
}

/**
 * Zmiana loginu, czyli adresu e-mail konta.
 *
 * ⚠ To zmiana **tożsamości**, nie danych kontaktowych: człowiek loguje się od tej chwili
 * czym innym, a serwer unieważnia jego sesje. Podpowiedź mówi to wprost, bo z samego pola
 * „adres e-mail" nikt tego nie odczyta.
 */
function LoginForm({ user }: { user: UserSummary }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const changeLogin = useChangeUserLogin();

  const [value, setValue] = useState(user.login);

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    const login = value.trim();

    if (login.length === 0 || login === user.login) {
      return;
    }

    changeLogin.mutate(
      { userId: user.id, login },
      {
        onSuccess: () =>
          show({ message: t('settings.users.loginChanged', { login }), tone: 'success' }),
        onError: (failure) => show({ message: describeError(failure), tone: 'danger' }),
      },
    );
  }

  return (
    <form className={styles['inlineForm']} onSubmit={onSubmit} noValidate>
      <span className={styles['grow']}>
        <Field
          label={t('settings.users.loginField')}
          type="email"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          hint={t('settings.users.loginHint')}
        />
      </span>

      <Button
        type="submit"
        variant="primary"
        disabled={changeLogin.isPending || value.trim() === user.login}
      >
        {t('settings.users.saveLogin')}
      </Button>
    </form>
  );
}

